/**
 * Générateur de mots de passe et de phrases de passe.
 *
 * Tout l'aléa provient de `crypto.getRandomValues` via `random.ts`, avec
 * échantillonnage par rejet : PAS de biais modulo, donc l'entropie annoncée est
 * l'entropie réelle. `Math.random` est interdit dans ce paquet.
 */
import { InvalidParameterError } from './errors.js';
import { randomInt, shuffleInPlace } from './random.js';
import { assertInteger } from './bytes.js';

/** Jeux de caractères par défaut. */
export const CHARSET = Object.freeze({
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  digits: '0123456789',
  /** Symboles ASCII sûrs : acceptés par la très grande majorité des sites. */
  symbols: '!#$%&()*+,-.:;<=>?@[]^_{|}~',
});

/**
 * Caractères visuellement ambigus, exclus sur demande pour les mots de passe
 * destinés à être recopiés à la main.
 */
export const AMBIGUOUS_CHARACTERS = 'Il1O0oB8S5Z2|`\'"{}[]()/\\';

export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 512;
export const DEFAULT_PASSWORD_LENGTH = 20;

export interface PasswordGeneratorOptions {
  /** Longueur en caractères (défaut 20, minimum 8). */
  readonly length?: number;
  readonly lowercase?: boolean;
  readonly uppercase?: boolean;
  readonly digits?: boolean;
  readonly symbols?: boolean;
  /** Minimum de caractères de chaque catégorie activée (défaut 1). */
  readonly minLowercase?: number;
  readonly minUppercase?: number;
  readonly minDigits?: number;
  readonly minSymbols?: number;
  /** Exclut les caractères visuellement ambigus. */
  readonly excludeAmbiguous?: boolean;
  /** Caractères supplémentaires à exclure (ex. contraintes d'un site). */
  readonly excludeCharacters?: string;
  /** Remplace le jeu de symboles par défaut. */
  readonly symbolCharacters?: string;
}

interface ResolvedCategory {
  readonly name: string;
  readonly pool: string[];
  readonly minimum: number;
}

function uniqueChars(source: string): string[] {
  // Découpage par point de code : évite de casser un caractère non-BMP.
  return [...new Set([...source])];
}

function removeExcluded(pool: string[], excluded: Set<string>): string[] {
  return pool.filter((c) => !excluded.has(c));
}

function resolveOptions(options: PasswordGeneratorOptions = {}) {
  if (options === null || typeof options !== 'object') {
    throw new InvalidParameterError('options: objet attendu');
  }
  const length = options.length ?? DEFAULT_PASSWORD_LENGTH;
  assertInteger(length, 'length', MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH);

  const useLower = options.lowercase ?? true;
  const useUpper = options.uppercase ?? true;
  const useDigits = options.digits ?? true;
  const useSymbols = options.symbols ?? true;

  if (!useLower && !useUpper && !useDigits && !useSymbols) {
    throw new InvalidParameterError('au moins une categorie de caracteres est requise');
  }

  const excluded = new Set<string>();
  if (options.excludeAmbiguous) for (const c of AMBIGUOUS_CHARACTERS) excluded.add(c);
  if (options.excludeCharacters !== undefined) {
    if (typeof options.excludeCharacters !== 'string') {
      throw new InvalidParameterError('excludeCharacters: chaine attendue');
    }
    for (const c of options.excludeCharacters) excluded.add(c);
  }

  if (options.symbolCharacters !== undefined && typeof options.symbolCharacters !== 'string') {
    throw new InvalidParameterError('symbolCharacters: chaine attendue');
  }
  const symbolSource = options.symbolCharacters ?? CHARSET.symbols;

  const categories: ResolvedCategory[] = [];
  const add = (name: string, enabled: boolean, source: string, minimum: number) => {
    if (!enabled) return;
    assertInteger(minimum, `min${name}`, 0, MAX_PASSWORD_LENGTH);
    const pool = removeExcluded(uniqueChars(source), excluded);
    if (pool.length === 0) {
      throw new InvalidParameterError(
        `categorie ${name}: jeu de caracteres vide apres exclusions`,
      );
    }
    categories.push({ name, pool, minimum });
  };

  add('Lowercase', useLower, CHARSET.lowercase, options.minLowercase ?? 1);
  add('Uppercase', useUpper, CHARSET.uppercase, options.minUppercase ?? 1);
  add('Digits', useDigits, CHARSET.digits, options.minDigits ?? 1);
  add('Symbols', useSymbols, symbolSource, options.minSymbols ?? 1);

  const totalMinimum = categories.reduce((sum, c) => sum + c.minimum, 0);
  if (totalMinimum > length) {
    throw new InvalidParameterError(
      `somme des minimums (${totalMinimum}) superieure a length (${length})`,
    );
  }

  const union = uniqueChars(categories.map((c) => c.pool.join('')).join(''));
  return { length, categories, union };
}

/**
 * Génère un mot de passe aléatoire.
 *
 * Déroulé : (1) tirage des caractères imposés par chaque minimum, (2) remplissage
 * depuis l'union des jeux, (3) mélange Fisher-Yates — pour que la position des
 * caractères imposés ne soit pas prévisible.
 */
export function generatePassword(options: PasswordGeneratorOptions = {}): string {
  const { length, categories, union } = resolveOptions(options);
  const chars: string[] = [];

  for (const category of categories) {
    for (let i = 0; i < category.minimum; i++) {
      chars.push(category.pool[randomInt(category.pool.length)]!);
    }
  }
  while (chars.length < length) {
    chars.push(union[randomInt(union.length)]!);
  }
  return shuffleInPlace(chars).join('');
}

/**
 * Entropie du mot de passe en bits : `length * log2(taille de l'union)`.
 *
 * ⚠️ BORNE SUPÉRIEURE. Imposer des minimums par catégorie restreint l'espace
 * des mots de passe et retire donc une fraction de bit à cette valeur.
 */
export function passwordEntropyBits(options: PasswordGeneratorOptions = {}): number {
  const { length, union } = resolveOptions(options);
  return length * Math.log2(union.length);
}

/** Taille minimale de liste de mots acceptée (10 bits d'entropie par mot). */
export const MIN_WORDLIST_SIZE = 1024;

export interface PassphraseGeneratorOptions {
  /**
   * Liste de mots. AUCUNE liste n'est embarquée : le cœur crypto reste sans
   * données volumineuses. Fournir une liste éprouvée (ex. EFF long, 7776 mots).
   */
  readonly wordlist: readonly string[];
  /** Nombre de mots (défaut 5, minimum 3). */
  readonly words?: number;
  /** Séparateur (défaut « - »). */
  readonly separator?: string;
  /** Capitalise la première lettre de chaque mot. */
  readonly capitalize?: boolean;
  /** Ajoute un chiffre aléatoire sur un mot choisi au hasard. */
  readonly includeNumber?: boolean;
}

/**
 * Valide une liste de mots : tableau, taille minimale, mots non vides et
 * **TOUS DISTINCTS**.
 *
 * ⚠️ L'unicité n'est pas une coquetterie. Le tirage est uniforme sur les
 * INDICES : si un mot apparaît deux fois, il sort deux fois plus souvent, la
 * distribution n'est plus uniforme sur les mots et l'entropie RÉELLE tombe
 * sous la valeur annoncée. Une liste dupliquée par accident (concaténation de
 * deux listes, import CSV) surestimerait donc silencieusement la force des
 * phrases de passe : on la refuse.
 */
function assertWordlist(wordlist: readonly string[]): void {
  if (!Array.isArray(wordlist)) {
    throw new InvalidParameterError('wordlist: tableau de mots attendu');
  }
  if (wordlist.length < MIN_WORDLIST_SIZE) {
    throw new InvalidParameterError(
      `wordlist: ${MIN_WORDLIST_SIZE} mots minimum, ${wordlist.length} fournis`,
    );
  }
  const distinct = new Set<string>();
  for (const word of wordlist) {
    if (typeof word !== 'string' || word.length === 0) {
      throw new InvalidParameterError('wordlist: mots non vides attendus');
    }
    distinct.add(word);
  }
  if (distinct.size !== wordlist.length) {
    throw new InvalidParameterError(
      `wordlist: mots dupliques (${wordlist.length - distinct.size}) — l entropie annoncee serait surestimee`,
    );
  }
}

/**
 * Génère une phrase de passe par tirage uniforme AVEC REMISE dans la liste
 * fournie (le tirage avec remise, sur une liste sans doublon, est ce qui rend
 * l'entropie exactement `words * log2(taille de liste)`).
 */
export function generatePassphrase(options: PassphraseGeneratorOptions): string {
  if (options === null || typeof options !== 'object') {
    throw new InvalidParameterError('options: objet attendu');
  }
  const { wordlist } = options;
  assertWordlist(wordlist);
  const words = options.words ?? 5;
  assertInteger(words, 'words', 3, 64);
  const separator = options.separator ?? '-';
  if (typeof separator !== 'string') {
    throw new InvalidParameterError('separator: chaine attendue');
  }

  const picked: string[] = [];
  for (let i = 0; i < words; i++) {
    const word = wordlist[randomInt(wordlist.length)]!;
    picked.push(options.capitalize ? word[0]!.toUpperCase() + word.slice(1) : word);
  }
  if (options.includeNumber) {
    const index = randomInt(picked.length);
    picked[index] = `${picked[index]}${randomInt(10)}`;
  }
  return picked.join(separator);
}

/**
 * Entropie exacte d'une phrase de passe, en bits (hors chiffre optionnel).
 *
 * Exacte, et non majorante comme {@link passwordEntropyBits} : la liste est
 * validée sans doublon, donc le tirage est réellement uniforme sur les mots.
 */
export function passphraseEntropyBits(options: PassphraseGeneratorOptions): number {
  if (options === null || typeof options !== 'object') {
    throw new InvalidParameterError('options: objet attendu');
  }
  assertWordlist(options.wordlist);
  const words = options.words ?? 5;
  assertInteger(words, 'words', 3, 64);
  return words * Math.log2(options.wordlist.length);
}
